<template>
  <v-card class="receipt-list full-height" :flat="flat">
    <v-card-title v-if="showTitle" :class="{ 'py-2': shrinkWithoutReceipts && !hasReceipts, 'pb-0 pt-2': denseHeader && !(shrinkWithoutReceipts && !hasReceipts) }">
      <div class="card-title">Kvitton</div>
      <template v-if="loading">
        <v-progress-circular
          indeterminate
          size="40"
          class="ml-4"
          color="primary"
        ></v-progress-circular>
      </template>
      <template v-else>
        <v-alert
          v-show="shrinkWithoutReceipts && !hasReceipts"
          dense
          text
          color="info"
          class="ma-0 ml-4 py-1"
        >
          Det finns inga kvitton
        </v-alert>
      </template>
    </v-card-title>

    <div class="overflow-y-auto" :style="{ height: scrollableTableHeight }">
      <v-data-table
        v-show="hasReceipts || !shrinkWithoutReceipts"
        :headers="headers"
        :items="tableRows"
        @click:row="receiptClicked"
        fixed-header
        :loading="loading"
        hide-default-footer
        disable-filtering
        disable-pagination
        :group-by="groupByBooking ? ['bookingId'] : []"
        :item-class="(item) => {
          return {
            'cursor-pointer': true
          }
        }"
      >
        <template v-slot:no-data>
          <template v-if="loading">
            <v-simple-table class="skeleton-loader-table">
              <tr v-for="i in 2" :key="i">
                <td v-for="(header, x) in headers" :key="x" :class="{ 'text-left': header.align === 'start', 'text-right': header.align === 'end' }">
                  <v-skeleton-loader
                    style=""
                    type="table-cell"
                    class=""
                  ></v-skeleton-loader>
                </td>
              </tr>
            </v-simple-table>
          </template>
          <template v-else>
            <v-alert
              dense
              text
              color="info"
              class="ma-10 text-center"
            >
              Det finns inga kvitton att visa
            </v-alert>
          </template>
        </template>

        <template v-slot:[`group.header`]="{ group: bookingId, isOpen, toggle }">
          <td :colspan="headers.length" class="grey lighten-4 cursor-pointer" @click="toggle">
            <div class="d-flex full-width">
              <v-icon class="mr-3">{{ isOpen ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
              <div class="subtitle-1 font-weight-medium my-auto">{{ getBookingTitle(bookingId) }}</div>
              <template v-if="getBookingIsDeleted(bookingId)">
                <v-chip
                  class="ml-2"
                  small
                  color="error"
                  text-color="white"
                >
                  Avbokad
                </v-chip>
              </template>
            </div>
          </td>
        </template>

        <template v-if="showTotalRow && hasReceipts" v-slot:[`body.append`]>
          <tr class="receipt-list-summary-row">
            <td
              v-for="header in headers"
              :key="header.value"
              :class="getSummaryCellClass(header)"
            >
              {{ getSummaryCellValue(header) }}
            </td>
          </tr>
        </template>
      </v-data-table>
    </div>
  </v-card>
</template>

<style scoped lang="scss">
.receipt-list-summary-row td {
  background-color: #fafafa;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  font-weight: 500;
}
</style>

<script>

export default {
  name: 'ReceiptList',
  components: {},
  props: {
    receipts: Array,
    loading: Boolean,
    showTitle: Boolean,
    denseHeader: Boolean,
    flat: Boolean,
    shrinkWithoutReceipts: Boolean,
    showTotalRow: {
      type: Boolean,
      default: false
    },
    groupByBooking: {
      type: Boolean,
      default: false
    },
    bookingGroup: {
      type: Object, // Används bara om groupByBooking är true
      default: null
    },
    bookings: {
      type: Array, // Används bara om groupByBooking är true
      default: () => []
    },
    // TODO: blivit av med stödet för displayedItems, fixa
    displayedItems: { // antal kvitton som visas innan det blir scroll, 0 för obegränsat
      type: Number,
      default: function () { return 0 }
    },
    displayedColumns: {
      type: Array,
      default: function () { return ['receipt_number', 'total', 'created_at'] } // möjliga värden: receipt_number, total, created_at
    }
  },
  data: () => ({}),
  computed: {
    headers: function () {
      const columns = []
      if (this.displayedColumns.includes('receipt_number')) {
        columns.push({ text: 'Kvittonummer', value: 'receiptSerial', align: 'start', sortable: false })
      }
      if (this.displayedColumns.includes('total')) {
        columns.push({ text: 'Belopp', value: 'totalFormatted', align: 'end', sortable: false })
      }
      if (this.displayedColumns.includes('created_at')) {
        columns.push({ text: 'Skapad', value: 'createdAtFormatted', align: 'start', sortable: false })
      }
      return columns
    },
    tableRows: function () {
      return this.receipts.map(receipt => {
        return {
          receiptId: receipt.id,
          bookingId: receipt.booking_id,
          receiptSerial: receipt.receipt_serial,
          totalFormatted: receipt.totalFormatted,
          createdAtFormatted: receipt.createdAtFormatted
        }
      })
    },
    totalAmountSummary: function () {
      return this.receipts.reduce((totalAmount, receipt) => {
        return totalAmount + (receipt.total || 0)
      }, 0)
    },
    totalAmountSummaryFormatted: function () {
      return window.conversion.toHumanReadableAmount(this.totalAmountSummary / 100)
    },
    summaryLabelColumnValue: function () {
      const labelColumn = this.headers.find(header => {
        return header.value !== 'totalFormatted'
      })
      return labelColumn?.value || null
    },
    receiptCountSummary: function () {
      const receiptCount = this.receipts.length
      return `${receiptCount} ${receiptCount === 1 ? 'kvitto' : 'kvitton'}`
    },
    hasReceipts: function () {
      return this.receipts.length > 0
    },
    scrollableTableHeight: function () {
      if (!this.hasReceipts && this.showTitle) {
        return '0px'
      }
      if (this.displayedItems === 0) {
        return '100%'
      }
      let requiredHeight = this.receipts.length * 48
      if (this.groupByBooking) {
        const uniqueBookingIds = new Set(this.receipts.map(inv => inv.booking_id))
        requiredHeight += uniqueBookingIds.size * 48 // lägger till höjden för gruppheaders
      }
      const maxHeight = this.displayedItems * 48
      let height = Math.min(requiredHeight, maxHeight)
      if (requiredHeight > maxHeight) {
        height += 24 // lägger till en halv rad så man tydligt ser att det finns fler kvitton att scrolla ner till
      }
      height += 48 // lägger till header höjden
      return `${height}px`
    }
  },
  methods: {
    receiptClicked: function ({ receiptId }) {
      this.openReceipt(receiptId)
    },
    openReceipt: function (receiptId) {
      this.$store.commit('posReceipt/SET_SHOW_DIALOG', false) // stänger dialog först, om dialogen redan skulle vara öppen i bakgrunden så flyttas den inte till förgrunden annars
      this.$nextTick(() => {
        this.$store.dispatch('posReceipt/openDialogById', { receiptId })
      })
    },
    getSummaryCellValue: function (header) {
      if (header.value === this.summaryLabelColumnValue) {
        return `Summa (${this.receiptCountSummary})`
      }
      if (header.value === 'totalFormatted') {
        return this.totalAmountSummaryFormatted
      }
      return ''
    },
    getSummaryCellClass: function (header) {
      return {
        'text-left': header.align === 'start' || header.value === this.summaryLabelColumnValue,
        'text-right': header.align === 'end' || header.value === 'totalFormatted'
      }
    },
    getBookingTitle: function (bookingId) {
      if (!bookingId) {
        return 'Kvitton på gruppbokningen'
      }
      const booking = this.bookings.find(b => b.id === bookingId)
      if (!booking) {
        return `Bokning #${bookingId}`
      }
      if (!this.bookingGroup?.hasMultipleCustomers) {
        // Behöver inte skriva ut kundnamnet om alla bokningar har samma kund
        return `${booking.booking_number}, ${booking.position.full_name}`
      }
      return `${booking.booking_number}, ${booking.position.full_name}, ${booking.customer.full_name}`
    },
    getBookingIsDeleted: function (bookingId) {
      if (!bookingId) {
        return false
      }
      const booking = this.bookings.find(b => b.id === bookingId)
      if (!booking) {
        return false
      }
      return !!booking.deleted_at
    }
  }
}
</script>
